import api from '@services/api';
import { DetailsRHEdit, DetailsRhTransfer, RatioRHEdit } from '../models/HumanResourcesApi';
import { UndoUnionDetailsRequest, UnitDetailsRHRequest } from '../models/details';
import {
  DetailsRhComplementResponse,
  DetailsRhExpensesRequest,
  DetailsRhExpensesResponse,
  DetailsRhExpensesUpdateRequest,
  HumanResourcesRequest,
  HumanResourcesResponse,
} from '../models/detailsRH';

/**
 * <p>
 * API endpoints that cover all human resources related CRUD calls
 */
export const HumanResourcesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    humanResourcesTransfer: builder.mutation<void, DetailsRhTransfer>({
      query: (project) => ({
        url: encodeURI(`/report/details-hr/transfer`),
        method: 'POST',
        body: project,
      }),
      invalidatesTags: ['SearchHumanResources'],
    }),
    updateHumanResources: builder.mutation<void, DetailsRHEdit>({
      query: (project) => ({
        url: encodeURI(`/report/details-hr/update`),
        method: 'PUT',
        body: project,
      }),
      invalidatesTags: ['SearchHumanResources'],
    }),
    updateRatioHumanResources: builder.mutation<void, RatioRHEdit>({
      query: (project) => ({
        url: encodeURI(`/report/details-hr/ratio/update`),
        method: 'PUT',
        body: project,
      }),
      invalidatesTags: ['SearchHumanResources'],
    }),
    UniteDetailsHumanResources: builder.mutation<void, UnitDetailsRHRequest>({
      query: (project) => ({
        url: encodeURI(`/report/details-hr/group`),
        method: 'POST',
        body: project,
      }),
      invalidatesTags: ['SearchHumanResources'],
    }),
    undoUnionDetailsHumanResources: builder.mutation<void, UndoUnionDetailsRequest>({
      query: (project) => ({
        url: encodeURI(`/report/details-hr/ungroup`),
        method: 'POST',
        body: project,
      }),
      invalidatesTags: ['SearchHumanResources'],
    }),
    searchHumanResources: builder.query<HumanResourcesResponse, HumanResourcesRequest>({
      query: (params: HumanResourcesRequest) => ({
        url: encodeURI(
          `report/details-hr/search?project=${params.project}&period=${params.period}&subGroup=${params.subGroup}&searchTerm=${params.searchTerm}`,
        ),
      }),
      providesTags: ['SearchHumanResources', 'SearchDetails'],
    }),
    humanResourcesExpense: builder.query<DetailsRhExpensesResponse, DetailsRhExpensesRequest>({
      query: (param) => {
        const id = param?.id;
        const bringTotalized = param?.bringTotalized ?? false;
        return {
          url: encodeURI(`/report/details-hr/expenses?id=${id}&bringTotalized=${bringTotalized}`),
        };
      },
      providesTags: ['SearchHumanResources'],
    }),
    humanResourcesComplement: builder.query<DetailsRhComplementResponse, number>({
      query: (number) => {
        return {
          url: encodeURI(`/report/details-hr/complementDetail?id=${number}`),
        };
      },
      providesTags: ['SearchHumanResources'],
    }),
    updateHumanResourcesExpense: builder.mutation<void, DetailsRhExpensesUpdateRequest>({
      query: (project) => ({
        url: encodeURI(`/report/details-hr/expenses/update`),
        method: 'PUT',
        body: project,
      }),
      invalidatesTags: ['SearchHumanResources'],
    }),
  }),
});

export const {
  useHumanResourcesTransferMutation,
  useUpdateHumanResourcesMutation,
  useUpdateRatioHumanResourcesMutation,
  useUndoUnionDetailsHumanResourcesMutation,
  useUniteDetailsHumanResourcesMutation,
  useSearchHumanResourcesQuery,
  useHumanResourcesExpenseQuery,
  useHumanResourcesComplementQuery,
  useUpdateHumanResourcesExpenseMutation,
} = HumanResourcesApi;
